import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, MinusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SupplySourceSelector from '../../components/supply/SupplySourceSelector';

const PublicRequestForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [showSupplySource, setShowSupplySource] = useState(false);
  const [supplySource, setSupplySource] = useState(null);

  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/api/products');
        setProducts(response.data.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Erreur lors du chargement des produits');
      }
    };

    fetchProducts();
  }, []);

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      organization: '',
      requestType: 'prestataire', // Valeur par défaut
      reason: '', // Gardé pour rétrocompatibilité
      selectedProducts: [{ productId: '', quantity: 1 }],
      additionalInfo: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Le nom est requis'),
      email: Yup.string().email('Email invalide').required('L\'email est requis'),
      phone: Yup.string().required('Le numéro de téléphone est requis'),
      organization: Yup.string().required('L\'organisation est requise'),
      requestType: Yup.string().required('Le motif de la demande est requis'),
      selectedProducts: Yup.array()
        .of(
          Yup.object().shape({
            productId: Yup.string().required('Produit requis'),
            quantity: Yup.number()
              .required('Quantité requise')
              .positive('La quantité doit être positive')
              .integer('La quantité doit être un nombre entier'),
          })
        )
        .min(1, 'Au moins un produit doit être sélectionné'),
      additionalInfo: Yup.string(),
    }),
    // Pas de validation pour la source d'approvisionnement (désactivée)
    onSubmit: async (values) => {
      try {
        // La source d'approvisionnement n'est plus requise pour les demandes Business Unit
        setLoading(true);
        
        // Create form data for file upload
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('email', values.email);
        formData.append('phone', values.phone);
        formData.append('organization', values.organization);
        formData.append('requestType', values.requestType);
        formData.append('reason', values.reason || values.requestType); // Utiliser le type comme raison si non spécifiée
        formData.append('selectedProducts', JSON.stringify(values.selectedProducts));
        formData.append('additionalInfo', values.additionalInfo);
        
        // Ajouter la source d'approvisionnement pour les demandes Business Unit (optionnel)
        if (values.requestType === 'business_unit' && supplySource) {
          // S'assurer que la source a la structure attendue
          const formattedSupplySource = {
            category: 'internal',
            source: {
              code: supplySource.source?.code || 'SIB',
              name: supplySource.source?.name || 'Site Industriel Bejaia'
            }
          };
          
          console.log('Envoi de la source d\'approvisionnement:', formattedSupplySource);
          formData.append('supplySource', JSON.stringify(formattedSupplySource));
        }
        
        selectedFiles.forEach((file) => {
          formData.append('attachment', file);
        });
        
        console.log('Soumission du formulaire avec requestType:', values.requestType);
        
        const response = await api.post('/api/requests', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        toast.success('Demande soumise avec succès');
        navigate(`/request/confirmation/${response.data.id}`);
      } catch (error) {
        console.error('Error submitting request:', error);
        toast.error(error.response?.data?.error || 'Erreur lors de la soumission de la demande');
      } finally {
        setLoading(false);
      }
    },
  });

  // Effet pour afficher/masquer le sélecteur de source d'approvisionnement
  useEffect(() => {
    setShowSupplySource(formik.values.requestType === 'business_unit');
  }, [formik.values.requestType]);
  
  // Gérer le changement de source d'approvisionnement
  const handleSupplySourceChange = (source) => {
    setSupplySource(source);
  };

  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 Mo par fichier
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // Permet de re-sélectionner le même fichier
    setFileError('');

    if (files.length === 0) return;

    const accepted = [];
    for (const file of files) {
      // Vérifier la taille (max 100 Mo)
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`Le fichier "${file.name}" est trop volumineux. Taille maximale: 100 Mo`);
        continue;
      }
      // Vérifier le type
      if (!ALLOWED_TYPES.includes(file.type)) {
        setFileError(`Le fichier "${file.name}" n'est pas supporté. Formats acceptés: PDF, JPEG, PNG`);
        continue;
      }
      accepted.push(file);
    }

    setSelectedFiles((prev) => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_FILES) {
        setFileError(`Vous pouvez joindre au maximum ${MAX_FILES} fichiers.`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  };

  const handleRemoveFile = (index) => {
    setFileError('');
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addProductRow = () => {
    formik.setFieldValue('selectedProducts', [
      ...formik.values.selectedProducts,
      { productId: '', quantity: 1 },
    ]);
  };

  const removeProductRow = (index) => {
    if (formik.values.selectedProducts.length > 1) {
      const updatedProducts = [...formik.values.selectedProducts];
      updatedProducts.splice(index, 1);
      formik.setFieldValue('selectedProducts', updatedProducts);
    }
  };

  const handleQuantityChange = (index, value) => {
    const newValue = Math.max(1, parseInt(value) || 0);
    const updatedProducts = [...formik.values.selectedProducts];
    updatedProducts[index].quantity = newValue;
    formik.setFieldValue('selectedProducts', updatedProducts);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <img
            className="mx-auto h-20 w-auto"
            src="/assets/logo.webp"
            alt="Tchin-Lait"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Demande de Produits
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Remplissez ce formulaire pour soumettre une demande de produits Tchin-Lait
          </p>
        </div>

        <div className="glassmorphism p-8 rounded-xl">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Informations personnelles */}
            <div className="bg-white bg-opacity-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="form-label">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-input ${
                      formik.touched.name && formik.errors.name ? 'border-red-500' : ''
                    }`}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <p className="form-error">{formik.errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="form-label">
                    Adresse email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-input ${
                      formik.touched.email && formik.errors.email ? 'border-red-500' : ''
                    }`}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="form-error">{formik.errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="form-label">
                    Numéro de téléphone
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    className={`form-input ${
                      formik.touched.phone && formik.errors.phone ? 'border-red-500' : ''
                    }`}
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <p className="form-error">{formik.errors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organization" className="form-label">
                    Organisation
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    className={`form-input ${
                      formik.touched.organization && formik.errors.organization ? 'border-red-500' : ''
                    }`}
                    value={formik.values.organization}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.organization && formik.errors.organization && (
                    <p className="form-error">{formik.errors.organization}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Informations de la demande */}
            <div className="bg-white bg-opacity-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de la demande</h3>
              
              <div className="mb-6">
                <label htmlFor="requestType" className="form-label">
                  Motif de la demande
                </label>
                <select
                  id="requestType"
                  name="requestType"
                  className={`form-input ${
                    formik.touched.requestType && formik.errors.requestType ? 'border-red-500' : ''
                  }`}
                  value={formik.values.requestType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="prestataire">Prestataire</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="association">Association</option>
                  <option value="consommateur">Consommateur</option>
                  <option value="business_unit">Business Unit</option>
                </select>
                {formik.touched.requestType && formik.errors.requestType && (
                  <p className="form-error">{formik.errors.requestType}</p>
                )}
              </div>
              
              {/* Afficher conditionnellement le sélecteur de source d'approvisionnement */}
              {showSupplySource && (
                <div className="mb-6">
                  <label className="form-label">Source d'approvisionnement (optionnel)</label>
                  <SupplySourceSelector
                    onChange={handleSupplySourceChange}
                    value={supplySource}
                    categoryFilter="internal" // Afficher uniquement les sites internes
                  />
                  <p className="mt-2 text-sm text-gray-600">
                    La sélection d'une source d'approvisionnement est optionnelle pour les demandes Business Unit.
                  </p>
                  {supplySource && (
                    <p className="mt-2 text-sm text-green-600">
                      Source sélectionnée: {supplySource.source?.name || 'Site Industriel Bejaia'} ({supplySource.source?.code || 'SIB'})
                    </p>
                  )}
                </div>
              )}

              <div className="mb-6">
                <label className="form-label">Produits demandés</label>
                
                {formik.values.selectedProducts.map((product, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <div className="flex-grow">
                      <select
                        name={`selectedProducts[${index}].productId`}
                        value={product.productId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`form-input ${
                          formik.touched.selectedProducts?.[index]?.productId && 
                          formik.errors.selectedProducts?.[index]?.productId 
                            ? 'border-red-500' 
                            : ''
                        }`}
                      >
                        <option value="">Sélectionnez un produit</option>
                        {Array.isArray(products) && products.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      {formik.touched.selectedProducts?.[index]?.productId && 
                       formik.errors.selectedProducts?.[index]?.productId && (
                        <p className="form-error">
                          {formik.errors.selectedProducts[index].productId}
                        </p>
                      )}
                    </div>
                    
                    <div className="w-32">
                      <div className="flex items-center">
                        <button
                          type="button"
                          className="bg-gray-200 p-1 rounded-l-md"
                          onClick={() => handleQuantityChange(index, product.quantity - 1)}
                        >
                          <MinusIcon className="h-5 w-5 text-gray-600" />
                        </button>
                        <input
                          type="number"
                          name={`selectedProducts[${index}].quantity`}
                          value={product.quantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          onBlur={formik.handleBlur}
                          className={`form-input rounded-none border-l-0 border-r-0 text-center ${
                            formik.touched.selectedProducts?.[index]?.quantity && 
                            formik.errors.selectedProducts?.[index]?.quantity 
                              ? 'border-red-500' 
                              : ''
                          }`}
                          min="1"
                        />
                        <button
                          type="button"
                          className="bg-gray-200 p-1 rounded-r-md"
                          onClick={() => handleQuantityChange(index, product.quantity + 1)}
                        >
                          <PlusIcon className="h-5 w-5 text-gray-600" />
                        </button>
                      </div>
                      {formik.touched.selectedProducts?.[index]?.quantity && 
                       formik.errors.selectedProducts?.[index]?.quantity && (
                        <p className="form-error">
                          {formik.errors.selectedProducts[index].quantity}
                        </p>
                      )}
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => removeProductRow(index)}
                      className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                      disabled={formik.values.selectedProducts.length === 1}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={addProductRow}
                  className="mt-2 flex items-center text-sm text-primary-600 hover:text-primary-800"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Ajouter un produit
                </button>
                
                {formik.touched.selectedProducts && 
                 typeof formik.errors.selectedProducts === 'string' && (
                  <p className="form-error mt-2">{formik.errors.selectedProducts}</p>
                )}
              </div>

              <div className="mb-6">
                <label htmlFor="additionalInfo" className="form-label">
                  Informations supplémentaires (optionnel)
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  rows={3}
                  className="form-input"
                  value={formik.values.additionalInfo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div>
                <label className="form-label">Pièce jointe (optionnel)</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                      >
                        <span>Télécharger un fichier</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          multiple
                          className="sr-only"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                      </label>
                      <p className="pl-1">ou glisser-déposer</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, PNG, JPG · jusqu'à 100 Mo par fichier · 5 fichiers max
                    </p>
                    {selectedFiles.length > 0 && (
                      <ul className="mt-2 w-full space-y-1 text-left">
                        {selectedFiles.map((file, index) => (
                          <li
                            key={`${file.name}-${index}`}
                            className="flex items-center justify-between rounded-md bg-green-50 px-3 py-1.5 text-sm text-green-700"
                          >
                            <span className="truncate pr-2">
                              {file.name}{' '}
                              <span className="text-green-500">
                                ({(file.size / (1024 * 1024)).toFixed(1)} Mo)
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(index)}
                              className="ml-2 flex-shrink-0 text-red-500 hover:text-red-700"
                              aria-label={`Retirer ${file.name}`}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {fileError && (
                      <p className="text-sm text-red-600">{fileError}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary py-3 px-8 text-base"
              >
                {loading ? 'Soumission en cours...' : 'Soumettre la demande'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicRequestForm;
